import type { Reducer } from 'redux';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import type { TradeInState, SingleTradeInItem, ReconItem } from '../types/tradeInTypes';
import type { ITradeIn } from '@makemydeal/dr-platform-types';
import * as tradesActionTypes from '../actionTypes/tradesActionTypes';
import { MERGE_FUEL_ECONOMY_FROM_API } from '../actionTypes/fuelEconomyActionTypes';
import { SOURCE_TYPE_DEALER } from '../constants';
import { extractTrimFromVehicles, extractTrimFromVehicleTitle } from '../utils/tradeVehicleTitle';
import { DealExchangeValidator } from '@makemydeal/dr-shared-ui-utils';
import type { MultipleTradeInsState, TradeInComponent } from '@makemydeal/dr-dash-types';
import { SAVE_AND_PRINT_TRADE_RECON_COST, SAVE_TRADE_RECON_COST } from '../actionTypes/tradeReconCostActionTypes';
import type { TradeReconData } from '../types/tradeReconCostTypes';
import type { MergeFuelEconomyFromApiPayload } from '../types/fuelEconomyTypes';
import { getCombinedFuelEconomyByVinMap, mergeFuelEconomyIntoTradeInItems } from '../utils/fuelEconomyMergeUtil';

// Re-export for backward compatibility
export type { SingleTradeInItem } from '../types/tradeInTypes';
export type { MultipleTradeInsState } from '@makemydeal/dr-dash-types';

export type TradeInsReducer = Reducer<MultipleTradeInsState>;

export const emptyTradeIn: TradeInState = {
    amountOwed: 0,
    bodyStyle: '',
    color: '',
    condition: '',
    doors: '',
    driveLine: '',
    engine: '',
    expirationDate: '',
    goodThroughDate: '',
    interiorColor: '',
    isAmountAppliedToFinancing: false,
    isCompleted: false,
    isSkipped: false,
    lienHolder: '',
    make: '',
    makeId: '',
    mileage: 0,
    model: '',
    modelId: '',
    offerDate: '',
    offerId: '',
    openManualForm: false,
    owed: 0,
    ownershipType: '',
    perDiemInterest: 0,
    isBuyerRegisteredOwner: true,
    isSellingVehicleRegisteredInSameState: true,
    source: '',
    status: '',
    stockNumber: '',
    transmission: '',
    trim: '',
    trimId: '',
    value: 0,
    vehicleId: '',
    vehicleOptions: {},
    vin: '',
    vinHasBeenDecoded: false,
    year: 0,
    tradeReconditionDetails: {
        tradeReconditionItems: [],
        tradeReconditionTotal: 0
    }
};

const getEmptyTradeInItem = (): SingleTradeInItem => {
    return {
        tradeIn: { ...emptyTradeIn },
        isExpanded: true,
        isDeleted: false,
        tradeInComponent: {} as any as TradeInComponent
    };
};

export const initialState: MultipleTradeInsState = {
    tradeInSummary: {
        netTrade: 0,
        totalAcv: 0,
        totalAllowance: 0,
        totalPayoff: 0,
        totalTradeInTaxReduction: 0 //TODO need to check for this total value
    },
    items: [getEmptyTradeInItem()]
};

export type TradeInsPayload = {
    tradeIns?: ITradeIn[];
    tradeInSummary?: Partial<MultipleTradeInsState['tradeInSummary']>;
};

export const buildTradeInsStateFromTrades = (
    state: MultipleTradeInsState,
    trades?: TradeInsPayload,
    isDealScopedNetTradeEnabled = false
): MultipleTradeInsState => {
    const backendTradeIns: ITradeIn[] = trades?.tradeIns || [];
    const backendSummary = trades?.tradeInSummary || {};

    if (backendTradeIns.length > 0) {
        return {
            ...state,
            tradeInSummary: {
                netTrade: backendSummary.netTrade || 0,
                totalAcv: backendSummary.totalAcv || 0,
                totalAllowance: backendSummary.totalAllowance || 0,
                totalPayoff: backendSummary.totalPayoff || 0,
                totalTradeInTaxReduction: backendSummary.totalTradeInTaxReduction || 0
            },
            items: backendTradeIns.map(
                (t: ITradeIn) =>
                    ({
                        ...getEmptyTradeInItem(),
                        tradeIn: isDealScopedNetTradeEnabled ? { ...emptyTradeIn, ...t } : t
                    } as SingleTradeInItem)
            )
        };
    }

    return { ...state, items: [{ ...getEmptyTradeInItem() } as SingleTradeInItem] };
};

const updateTradeInOnItemIndex = ({
    state,
    update,
    index
}: {
    state: MultipleTradeInsState;
    update: Partial<TradeInState>;
    index: number;
}) => {
    return {
        ...state,
        items: state.items.map((item, loopIndex) =>
            loopIndex === index ? { ...item, tradeIn: { ...item.tradeIn, ...update } } : item
        )
    };
};

const updateTradeInComponentOnItemIndex = ({
    state,
    index,
    update
}: {
    state: MultipleTradeInsState;
    index: number;
    update: Partial<TradeInComponent>;
}) => {
    return {
        ...state,
        items: state.items.map((item, loopIndex) =>
            loopIndex === index ? { ...item, tradeInComponent: { ...(item || {}).tradeInComponent, ...update } } : item
        )
    };
};

const getPreviousTradeIn = (state: MultipleTradeInsState, index: number) => {
    return (state.items[index] || {}).tradeIn || {};
};

const getPreviousVehicle = (state: MultipleTradeInsState, index: number) => {
    return ((state.items[index] || {}).tradeInComponent || {}).vehicle || {};
};

const getPreviousLookups = (state: MultipleTradeInsState, index: number) => {
    return ((state.items[index] || {}).tradeInComponent || {}).lookups || {};
};

export const reducer: TradeInsReducer = (state: MultipleTradeInsState = initialState, action: any) => {
    switch (action.type) {
        case initActionTypes.INIT_PENCIL_SUCCESS:
        case initActionTypes.INIT_PENCIL_INVALID_DEAL: {
            return buildTradeInsStateFromTrades(
                state,
                action.payload.trades,
                action.payload.featureToggles?.DE769035_Enable_Deal_Scoped_Net_Trade
            );
        }

        case tradesActionTypes.ADD_TRADE_IN: {
            const errorIndices = action.payload?.errorIndices || [];
            return {
                ...state,
                items: [
                    ...state.items.map((item, index) => ({
                        ...item,
                        isExpanded: errorIndices.includes(index) ? item.isExpanded : false
                    })),
                    { ...getEmptyTradeInItem(), isExpanded: true }
                ]
            };
        }

        case tradesActionTypes.DELETE_TRADE_IN: {
            return {
                ...state,
                items: state.items.map((item, index) => {
                    return index === action.payload ? { ...item, isDeleted: true } : item;
                })
            };
        }

        case tradesActionTypes.UNDO_DELETED_TRADE_IN: {
            return {
                ...state,
                items: state.items.map((item, index) => {
                    return index === action.payload ? { ...item, isDeleted: false } : item;
                })
            };
        }

        case tradesActionTypes.TOGGLE_TRADE_IN_ACCORDION_STATE:
            return {
                ...state,
                items: state.items.map((item, index) => {
                    return index === action.payload ? { ...item, isExpanded: !item.isExpanded } : item;
                })
            };

        case tradesActionTypes.TRADE_INS_UPDATE_DOORS:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { doors: action.payload.doors }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_LIENHOLDER_FULL: {
            const { index, ...lienholderFull } = action.payload;
            return updateTradeInOnItemIndex({ state, index, update: lienholderFull });
        }

        case tradesActionTypes.TRADE_INS_ALLOWANCE_CHANGED: {
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: {
                    value: action.payload.value,
                    ...(action.meta?.isAllowanceSourceIndicatorEnabled ? { source: SOURCE_TYPE_DEALER } : {})
                }
            });
        }

        case tradesActionTypes.TRADE_INS_PAYOFF_CHANGED: {
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { owed: action.payload.owed, amountOwed: action.payload.owed }
            });
        }

        case tradesActionTypes.TRADE_INS_ACV_CHANGED: {
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { acv: action.payload.acv }
            });
        }

        case tradesActionTypes.TRADE_INS_UPDATE_IS_BUYER_REGISTERED_OWNER:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { isBuyerRegisteredOwner: action.payload.isBuyerRegisteredOwner }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_IS_SELLING_VEHICLE_REGISTERED_IN_SAME_STATE:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { isSellingVehicleRegisteredInSameState: action.payload.isSellingVehicleRegisteredInSameState }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_OWNERSHIP_TYPE:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { ownershipType: action.payload.ownershipType }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_DRIVE_LINE:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { driveLine: action.payload.driveLine }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_ODOMETER:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { mileage: action.payload.mileage }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_STOCK_NUMBER:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { stockNumber: action.payload.stockNumber }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_YEAR:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { year: action.payload.year }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_MAKE:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { make: action.payload.make, makeId: action.payload.makeId }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_MAKE_ID:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { makeId: action.payload.makeId.toString() }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_MODEL:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { model: action.payload.model, modelId: action.payload.modelId.toString() }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_MODEL_ID:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { modelId: action.payload.modelId.toString() }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_TRIM:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { trim: action.payload.trim, trimId: action.payload.trimId }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_EXTERIOR_COLOR:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { color: action.payload.color }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_CONDITION:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { condition: action.payload.condition }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_ENGINE:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { engine: action.payload.engine }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_TRANSMISSION:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { transmission: action.payload.transmission }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_BODY_STYLE:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { bodyStyle: action.payload.bodyStyle }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_INTERIOR_COLOR:
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: { interiorColor: action.payload.interiorColor }
            });

        case tradesActionTypes.TRADE_INS_UPDATE_VIN: {
            const previousTradeIn = getPreviousTradeIn(state, action.meta.index);
            const vin: string = action.payload.vin || '';
            return updateTradeInOnItemIndex({
                state,
                index: action.payload.index,
                update: {
                    vin,
                    vinHasBeenDecoded: previousTradeIn.vinHasBeenDecoded ? !(vin.length === 0) : false,
                    ...(vin.length === 0 && {
                        bodyStyle: '',
                        color: '',
                        driveLine: '',
                        engine: '',
                        hasMultipleVin: false,
                        interiorColor: '',
                        make: '',
                        makeId: '',
                        mileage: 0,
                        model: '',
                        modelId: '',
                        stockNumber: '',
                        transmission: '',
                        trim: '',
                        trimId: '',
                        vehicleId: '',
                        year: 0,
                        multipleVinOptions: [],
                        trimDecodedOptions: []
                    })
                }
            });
        }

        case tradesActionTypes.TRADE_INS_VIN_REQUEST: {
            return updateTradeInOnItemIndex({
                state,
                index: action.meta.index,
                update: { errorDecodingVin: false }
            });
        }

        case tradesActionTypes.TRADE_INS_VIN_SUCCESS: {
            let newTradeInState = {};
            const previousTradeIn = getPreviousTradeIn(state, action.meta.index);
            const isPreserveCrmTradeVehicleInfoEnabled = Boolean(action.meta.isPreserveCrmTradeVehicleInfoEnabled);
            if (action.payload.length === 0) {
                newTradeInState = {
                    errorDecodingVin: true
                };
            } else if (action.payload.length > 1) {
                newTradeInState = {
                    hasMultipleVin: true,
                    multipleVinOptions: action.payload,
                    trimDecodedOptions: extractTrimFromVehicles(action.payload, isPreserveCrmTradeVehicleInfoEnabled),
                    vinHasBeenDecoded: false
                };
            } else {
                const { year, make, model, trim, id: vehicleId, bodyDescription, powertrain, title } = action.payload[0];

                newTradeInState = {
                    bodyStyle: bodyDescription.primaryBodyStyle,
                    driveLine: DealExchangeValidator.driveLine(powertrain?.drivetrain?.description) || '',
                    engine: powertrain?.engine?.primaryDescription || '',
                    make,
                    makeId: previousTradeIn.makeId,
                    model: model.normalizedName,
                    modelId: previousTradeIn.modelId,
                    transmission: powertrain?.transmission?.primaryDescription || '',
                    trim: isPreserveCrmTradeVehicleInfoEnabled
                        ? trim.normalizedName || ''
                        : extractTrimFromVehicleTitle(title.split(' ')),
                    trimId: trim.normalizedTrimId.toString(),
                    trimDecodedOptions: extractTrimFromVehicles(action.payload, isPreserveCrmTradeVehicleInfoEnabled),
                    vehicleId: vehicleId.toString(),
                    vinHasBeenDecoded: true,
                    year
                };
            }

            return {
                ...state,
                items: state.items.map((item, index) => {
                    return index === action.meta.index ? { ...item, tradeIn: { ...previousTradeIn, ...newTradeInState } } : item;
                })
            };
        }
        case tradesActionTypes.TRADE_INS_VIN_FAILURE: {
            return updateTradeInOnItemIndex({
                state,
                index: action.meta.index,
                update: { errorDecodingVin: true }
            });
        }

        case tradesActionTypes.TRADE_INS_MULTIPLE_VIN_SELECTED: {
            const previousTradeIn = getPreviousTradeIn(state, action.payload.index);
            const { year, make, model, trim, id: vehicleId, bodyDescription, powertrain, title } = action.payload.option;
            const newTradeInState = {
                bodyStyle: bodyDescription.primaryBodyStyle,
                driveLine: DealExchangeValidator.driveLine(powertrain?.drivetrain?.description) || '',
                engine: powertrain?.engine?.primaryDescription || '',
                make,
                makeId: previousTradeIn.makeId,
                model: model.normalizedName,
                modelId: previousTradeIn.modelId,
                hasMultipleVin: false,
                transmission: powertrain?.transmission?.primaryDescription || '',
                trim: action.meta?.isPreserveCrmTradeVehicleInfoEnabled
                    ? trim.normalizedName || ''
                    : extractTrimFromVehicleTitle(title.split(' ')),
                trimId: trim.normalizedTrimId.toString(),
                vehicleId: vehicleId.toString(),
                vinHasBeenDecoded: true,
                year
            };
            return {
                ...state,
                items: state.items.map((item, index) => {
                    return index === action.payload.index ? { ...item, tradeIn: { ...previousTradeIn, ...newTradeInState } } : item;
                })
            };
        }

        case tradesActionTypes.TRADE_INS_COMPONENT_UPDATE_VEHICLE: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.payload.index,
                update: { vehicle: { ...getPreviousVehicle(state, action.meta.index), ...action.payload.vehicle } }
            });
        }

        case MERGE_FUEL_ECONOMY_FROM_API: {
            const combinedFuelEconomyByVin = getCombinedFuelEconomyByVinMap(
                (action.payload as MergeFuelEconomyFromApiPayload | undefined)?.tradeInVehicles
            );
            if (!combinedFuelEconomyByVin.size) return state;
            const items = mergeFuelEconomyIntoTradeInItems(state.items, combinedFuelEconomyByVin);
            return items !== state.items ? { ...state, items } : state;
        }

        case tradesActionTypes.TRADE_INS_MAKES_REQUEST: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    lookups: {
                        ...getPreviousLookups(state, action.meta.index),
                        makes: { isLoading: true, options: [] }
                    } as TradeInComponent['lookups']
                }
            });
        }

        case tradesActionTypes.TRADE_INS_MAKES_SUCCESS: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    lookups: {
                        ...getPreviousLookups(state, action.meta.index),
                        makes: { isLoading: false, options: [...(action.payload || [])] }
                    } as TradeInComponent['lookups']
                }
            });
        }

        case tradesActionTypes.TRADE_INS_MODELS_REQUEST: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    lookups: {
                        ...getPreviousLookups(state, action.meta.index),
                        models: { isLoading: true, options: [] }
                    } as TradeInComponent['lookups']
                }
            });
        }

        case tradesActionTypes.TRADE_INS_MODELS_SUCCESS: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    lookups: {
                        ...getPreviousLookups(state, action.meta.index),
                        models: { isLoading: false, options: [...(action.payload || [])] }
                    } as TradeInComponent['lookups']
                }
            });
        }

        case tradesActionTypes.TRADE_INS_TRIMS_REQUEST: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    lookups: {
                        ...getPreviousLookups(state, action.meta.index),
                        trims: { isLoading: true, options: [] }
                    } as TradeInComponent['lookups']
                }
            });
        }

        case tradesActionTypes.TRADE_INS_TRIMS_SUCCESS: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    lookups: {
                        ...getPreviousLookups(state, action.meta.index),
                        trims: { isLoading: false, options: [...(action.payload || [])] }
                    } as TradeInComponent['lookups']
                }
            });
        }

        case tradesActionTypes.TRADE_INS_VEHICLES_SUCCESS: {
            return updateTradeInComponentOnItemIndex({
                state,
                index: action.meta.index,
                update: {
                    ownership: 'placeholder_ownership',
                    vehicle: {
                        ...getPreviousVehicle(state, action.meta.index),
                        trim: {
                            id: action.payload.data?.trimId?.toString() || '',
                            name: action.payload.data?.trimName || '',
                            vehicleId: `${action.payload.data?.vehicleId}`
                        }
                    } as TradeInComponent['vehicle']
                }
            });
        }
        case SAVE_AND_PRINT_TRADE_RECON_COST:
        case SAVE_TRADE_RECON_COST: {
            return {
                ...state,
                items: state.items.map((item, index) => {
                    const tradeKey = `trade-${index + 1}`;
                    const tradeReconData: TradeReconData = action.payload[tradeKey];
                    return {
                        ...item,
                        tradeIn: {
                            ...item.tradeIn,
                            acv: tradeReconData?.acv || item.tradeIn.acv,
                            value: tradeReconData?.value || item.tradeIn.value,
                            amountOwed: tradeReconData?.amountOwed || item.tradeIn.amountOwed,
                            tradeReconditionDetails: {
                                tradeReconditionItems:
                                    (tradeReconData?.tradeReconditionItems.map((reconItem) => ({
                                        reconditionName: reconItem.reconditionName,
                                        reconditionAmount: reconItem.reconditionAmount
                                    })) as ReconItem[]) || [],
                                tradeReconditionTotal: tradeReconData?.tradeReconditionTotal || 0
                            }
                        }
                    };
                })
            };
        }

        default: {
            return state;
        }
    }
};
